'use client';

import {
  Activity,
  Archive,
  Brain,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Globe,
  MessageSquare,
  Paperclip,
  ShieldAlert,
  ShieldCheck,
  User,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAdminUsageTimeseriesQuery, useAdminUserDetailQuery } from '../hooks/use-gateway-queries';
import { cn } from '../utils/cn';
import {
  cartesianGridProps,
  getChartColors,
  getUserColor,
  tooltipContainerStyle,
} from '../utils/chart-theme';
import {
  formatBucketRangeLabel,
  seriesForChartRange,
  type MetricKey,
} from '../utils/chart-series';
import {
  ActivityHeatmapGrid,
  defaultTwoMonthStart,
  formatTwoMonthLabel,
  formatWeekLabel,
  HeatmapPeriodNav,
  shiftDateKey,
  shiftMonthKey,
  shiftWeekStart,
  twoMonthRange,
  weekStartSunday,
} from './activity-heatmap';
import { Dialog } from './dialog';
import { InfoRow, InfoSection } from './info-section';
import { Badge, CompositionBar, EmptyState, StatCard, formatDate, formatRelative } from './admin-panel';

// ─── Internal layout primitives ──────────────────────────────────────────────

const ModalSection = ({
  title,
  icon,
  children,
  className,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('flex flex-col gap-3', className)}>
    <div className="flex items-center gap-2 text-muted-foreground/70">
      {icon}
      <span className="text-[11px] font-semibold uppercase tracking-wider">{title}</span>
    </div>
    {children}
  </div>
);

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const HeatmapPanel = ({
  title,
  icon,
  nav,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  nav: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="flex h-full min-h-0 w-full flex-col">
    <div className="flex flex-shrink-0 items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2 text-muted-foreground/70">
        {icon}
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider">{title}</span>
      </div>
      {nav}
    </div>
    <div className="min-h-0 flex-1" aria-hidden />
    <div className="flex-shrink-0">{children}</div>
  </div>
);

type DailyPoint = { date: string; messages: number; sessions: number; files: number };

type RangeKey = '7d' | '30d' | '90d';

const RANGE_OPTIONS: { key: RangeKey; label: string; windowDays: number; stepDays: number }[] = [
  { key: '7d', label: '7d', windowDays: 7, stepDays: 7 },
  { key: '30d', label: '30d', windowDays: 30, stepDays: 30 },
  { key: '90d', label: '90d', windowDays: 90, stepDays: 90 },
];

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function shortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatPeriodLabel(start: string, end: string): string {
  try {
    const startDate = new Date(`${start}T00:00:00.000Z`);
    const endDate = new Date(`${end}T00:00:00.000Z`);
    const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
    const fmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
    const endFmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${fmt.format(startDate)} – ${endFmt.format(endDate)}`;
  } catch {
    return `${start} – ${end}`;
  }
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

const ModalSkeleton = () => (
  <div className="flex flex-col gap-6 p-6 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-full bg-muted/40" />
      <div className="flex flex-col gap-2">
        <div className="h-4 w-40 rounded bg-muted/40" />
        <div className="h-3 w-56 rounded bg-muted/30" />
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-20 rounded-xl bg-muted/30" />
      ))}
    </div>
    <div className="h-32 rounded-xl bg-muted/20" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="h-28 rounded-xl bg-muted/20" />
      <div className="h-28 rounded-xl bg-muted/20" />
    </div>
  </div>
);

// ─── Trend chart (daily series) ───────────────────────────────────────────────

const METRIC_OPTIONS: { key: MetricKey; label: string }[] = [
  { key: 'messages', label: 'Messages' },
  { key: 'sessions', label: 'Sessions' },
  { key: 'files', label: 'Files' },
];

function buildDateRange(end: string, days: number): string[] {
  return Array.from({ length: days }, (_, i) => shiftDateKey(end, -(days - 1 - i)));
}

const TrendChartSkeleton = () => (
  <div className="flex flex-col gap-3 animate-pulse">
    <div className="h-7 w-48 rounded-md bg-muted/40" />
    <div className="flex justify-between gap-3">
      <div className="h-7 w-28 rounded-xl bg-muted/30" />
      <div className="h-7 w-36 rounded-lg bg-muted/30" />
    </div>
    <div className="h-[120px] rounded-xl bg-muted/20" />
  </div>
);

const DEFAULT_RANGE = RANGE_OPTIONS[1]!;

/** Self-contained activity trend — fetches timeseries independently so range/period changes don't reload the modal. */
const TrendChart = ({ userId }: { userId: string }) => {
  const [metric, setMetric] = React.useState<MetricKey>('messages');
  const [rangeKey, setRangeKey] = React.useState<RangeKey>('30d');
  const [periodEnd, setPeriodEnd] = React.useState(todayKey);

  const selectedRange = RANGE_OPTIONS.find(r => r.key === rangeKey) ?? DEFAULT_RANGE;
  const { windowDays, stepDays } = selectedRange;

  const today = todayKey();
  const isCurrentPeriod = periodEnd >= today;
  const apiEndDate = isCurrentPeriod ? undefined : periodEnd;

  const { data, isPending, isFetching, isError } = useAdminUsageTimeseriesQuery(
    windowDays,
    apiEndDate,
    !!userId
  );

  const body = data?.status === 200 ? data.body : undefined;
  const apiSeries = body?.series ?? [];

  const rangeEnd = body?.range?.end ?? (isCurrentPeriod ? today : periodEnd);
  const rangeStart = body?.range?.start ?? shiftDateKey(rangeEnd, -(windowDays - 1));
  const periodLabel = formatPeriodLabel(rangeStart, rangeEnd);

  const userDailySeries = React.useMemo((): DailyPoint[] => {
    if (apiSeries.length > 0) {
      const slice =
        apiSeries.length === windowDays ? apiSeries : apiSeries.slice(-windowDays);
      return slice.map(day => {
        const u = day.perUser.find(p => p.userId === userId);
        return {
          date: day.date,
          messages: u?.messages ?? 0,
          sessions: u?.sessions ?? 0,
          files: u?.files ?? 0,
        };
      });
    }
    return buildDateRange(rangeEnd, windowDays).map(date => ({
      date,
      messages: 0,
      sessions: 0,
      files: 0,
    }));
  }, [apiSeries, userId, windowDays, rangeEnd]);

  const isAggregated = rangeKey !== '7d';
  const colors = getChartColors();
  const color = getUserColor(0);

  const chartSeries = React.useMemo(() => {
    const asOrgSeries = userDailySeries.map(d => ({
      date: d.date,
      perUser: [{ userId, messages: d.messages, sessions: d.sessions, files: d.files }],
    }));
    return seriesForChartRange(asOrgSeries, rangeKey);
  }, [userDailySeries, rangeKey, userId]);

  const chartData = React.useMemo(
    () =>
      chartSeries.map(bucket => {
        const row = bucket.perUser.find(u => u.userId === userId);
        return {
          date: bucket.date,
          dateStart: bucket.dateStart,
          dateEnd: bucket.dateEnd,
          bucketSize: bucket.bucketSize,
          dateLabel: formatBucketRangeLabel(bucket.dateStart, bucket.dateEnd, bucket.bucketSize),
          value: row?.[metric] ?? 0,
        };
      }),
    [chartSeries, userId, metric]
  );

  const handleRangeChange = (nextKey: RangeKey) => {
    setRangeKey(nextKey);
    setPeriodEnd(todayKey());
  };

  const shiftPeriod = (direction: -1 | 1) => {
    setPeriodEnd(prev => {
      const anchor = prev >= today ? today : prev;
      const next = shiftDateKey(anchor, direction * stepDays);
      return next > today ? today : next;
    });
  };

  if (isPending && !body) return <TrendChartSkeleton />;

  if (isError) {
    return (
      <div className="flex items-center justify-center h-28 text-xs text-muted-foreground/60 rounded-xl border border-border/40 bg-bg-neutral-8">
        Could not load activity trend.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1 text-[10px] rounded-md overflow-hidden border border-border/40 bg-muted/20 w-fit">
        {METRIC_OPTIONS.map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setMetric(opt.key)}
            className={cn(
              'px-2.5 py-1 font-medium transition-colors',
              metric === opt.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleRangeChange(opt.key)}
              className={cn(
                'px-2.5 py-1 rounded-xl text-[10px] font-medium transition-all duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                rangeKey === opt.key
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => shiftPeriod(-1)}
            title="Previous period"
            className={cn(
              'p-1.5 rounded-lg text-muted-foreground transition-colors',
              'hover:text-foreground hover:bg-muted/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30'
            )}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[120px] text-center text-[10px] font-medium text-foreground tabular-nums">
            {periodLabel}
          </span>
          <button
            type="button"
            onClick={() => shiftPeriod(1)}
            disabled={isCurrentPeriod}
            title="Next period"
            className={cn(
              'p-1.5 rounded-lg text-muted-foreground transition-colors',
              'hover:text-foreground hover:bg-muted/60',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
              'disabled:opacity-30 disabled:pointer-events-none'
            )}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
        <ResponsiveContainer width="100%" height={120}>
          <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
            <defs>
              <linearGradient id="ud-area-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...cartesianGridProps} stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fill: colors.axis }}
              tickFormatter={shortDate}
              interval="preserveStartEnd"
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fill: colors.axis }}
              allowDecimals={isAggregated}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={tooltipContainerStyle()}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload as { dateLabel?: string; date?: string } | undefined;
                return row?.dateLabel ?? (row?.date ? shortDate(row.date) : '');
              }}
              formatter={(value: number) => [
                isAggregated
                  ? value.toLocaleString(undefined, { maximumFractionDigits: 1 })
                  : value.toLocaleString(),
                METRIC_OPTIONS.find(m => m.key === metric)?.label ?? metric,
              ]}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={1.5}
              fill="url(#ud-area-grad)"
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ─── Weekly hourly heatmap (day × hour, 7 rows) ───────────────────────────────

const HourlyWeekHeatmap = ({
  hourlyByDay,
}: {
  hourlyByDay: { date: string; hours: number[] }[];
}) => {
  const [weekStart, setWeekStart] = React.useState(() => weekStartSunday());
  const today = new Date().toISOString().slice(0, 10);
  const canGoNext = shiftDateKey(weekStart, 6) < today;

  const byDate = React.useMemo(
    () => new Map(hourlyByDay.map(d => [d.date, d.hours])),
    [hourlyByDay]
  );

  const rows = React.useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = shiftDateKey(weekStart, i);
      const d = new Date(`${date}T00:00:00.000Z`);
      const dayLabel = new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(d);
      return {
        id: date,
        label: dayLabel,
        values: byDate.get(date) ?? (new Array(24).fill(0) as number[]),
      };
    });
  }, [weekStart, byDate]);

  const columnLabels = Array.from({ length: 24 }, (_, h) =>
    h % 3 === 0 ? h.toString().padStart(2, '0') : ''
  );

  return (
    <HeatmapPanel
      title="Activity by Hour (UTC)"
      icon={<Clock size={12} className="flex-shrink-0" />}
      nav={
        <HeatmapPeriodNav
          label={formatWeekLabel(weekStart)}
          onPrev={() => setWeekStart(w => shiftWeekStart(w, -1))}
          onNext={() => setWeekStart(w => shiftWeekStart(w, 1))}
          canGoNext={canGoNext}
        />
      }
    >
      <ActivityHeatmapGrid
        rows={rows}
        columnLabels={columnLabels}
        unit="messages"
        color={getUserColor(1)}
        showNav={false}
      />
    </HeatmapPanel>
  );
};

// ─── Bi-monthly weekly heatmap (week × day-of-week) ───────────────────────────

const WeeklyDayHeatmap = ({
  weeklyActivity,
}: {
  weeklyActivity: { weekStart: string; days: number[] }[];
}) => {
  const [periodStart, setPeriodStart] = React.useState(defaultTwoMonthStart);
  const { start, end } = twoMonthRange(periodStart);
  const today = new Date().toISOString().slice(0, 10);
  const canGoNext = end < today;

  const rows = React.useMemo(() => {
    return weeklyActivity
      .filter(w => {
        const weekEnd = shiftDateKey(w.weekStart, 6);
        return w.weekStart <= end && weekEnd >= start;
      })
      .map(w => ({
        id: w.weekStart,
        label: shortDate(w.weekStart),
        values: w.days,
      }));
  }, [weeklyActivity, start, end]);

  return (
    <HeatmapPanel
      title="Activity by Day of Week"
      icon={<Calendar size={12} className="flex-shrink-0" />}
      nav={
        <HeatmapPeriodNav
          label={formatTwoMonthLabel(periodStart)}
          onPrev={() => setPeriodStart(k => shiftMonthKey(k, -2))}
          onNext={() => setPeriodStart(k => shiftMonthKey(k, 2))}
          canGoNext={canGoNext}
        />
      }
    >
      <ActivityHeatmapGrid
        rows={rows}
        columnLabels={[...DAY_LABELS]}
        unit="messages"
        color={getUserColor(2)}
        showNav={false}
      />
    </HeatmapPanel>
  );
};

// ─── Specialists chart ────────────────────────────────────────────────────────

const SpecialistsChart = ({
  specialists,
}: {
  specialists: { skillId: string; name: string; sessions: number; messages: number }[];
}) => {
  if (!specialists.length) {
    return (
      <EmptyState
        icon={<Brain size={20} />}
        title="No specialist usage"
        hint="Sessions with AI specialists will appear here."
      />
    );
  }
  const max = Math.max(...specialists.map(s => s.sessions), 1);
  return (
    <div className="flex flex-col gap-2">
      {specialists.map((s, i) => (
        <div key={s.skillId} className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground w-28 truncate flex-shrink-0" title={s.name}>
            {s.name}
          </span>
          <div className="flex-1 h-2 bg-muted/30 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(s.sessions / max) * 100}%`,
                background: getUserColor(i),
              }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground tabular-nums w-8 text-right">{s.sessions}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

type UserDetailModalProps = {
  userId: string | null;
  onClose: () => void;
};

export const UserDetailModal = ({ userId, onClose }: UserDetailModalProps) => {
  const { data, isPending, isError } = useAdminUserDetailQuery(userId);

  const detail = data?.status === 200 ? data.body : null;

  const initials = React.useMemo(() => {
    if (!detail) return '?';
    const { firstName, lastName, email } = detail.profile;
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
    if (firstName) return firstName.slice(0, 2).toUpperCase();
    return email.slice(0, 2).toUpperCase();
  }, [detail]);

  const displayName = React.useMemo(() => {
    if (!detail) return '';
    const { firstName, lastName, email } = detail.profile;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    return email.split('@')[0] || email;
  }, [detail]);

  const engagementItems = React.useMemo(() => {
    if (!detail) return [];
    const { totalUserMessages, totalAiMessages } = detail.chat;
    return [
      { label: 'You', value: totalUserMessages, color: getUserColor(0) },
      { label: 'AI', value: totalAiMessages, color: getUserColor(1) },
    ].filter(i => i.value > 0);
  }, [detail]);

  return (
    <Dialog isOpen={!!userId} onClose={onClose} size="xl" className="max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 p-6 border-b border-border/40 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
        {detail ? (
          <div className="flex items-center gap-3 min-w-0">
            {detail.profile.profilePictureUrl ? (
              <img
                src={detail.profile.profilePictureUrl}
                alt={displayName}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-semibold truncate">{displayName}</h2>
                <Badge tone={detail.profile.role === 'admin' ? 'primary' : 'neutral'}>
                  {detail.profile.role === 'admin' ? 'Admin' : 'Member'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">{detail.profile.email}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-muted/40 animate-pulse" />
            <div className="flex flex-col gap-1.5">
              <div className="h-4 w-32 rounded bg-muted/40 animate-pulse" />
              <div className="h-3 w-48 rounded bg-muted/30 animate-pulse" />
            </div>
          </div>
        )}
        <button
          onClick={onClose}
          className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Body */}
      {isPending && <ModalSkeleton />}

      {isError && !isPending && (
        <div className="p-6">
          <EmptyState
            icon={<User size={24} />}
            title="Could not load user details"
            hint="An error occurred while fetching data. Please try again."
          />
        </div>
      )}

      {!isPending && !isError && !detail && (
        <div className="p-6">
          <EmptyState icon={<User size={24} />} title="No data found" hint="This user has no activity data yet." />
        </div>
      )}

      {detail && (
        <div className="flex flex-col gap-6 p-6">
          {/* Identity & access */}
          <ModalSection title="Identity & Access" icon={<User size={12} />}>
            <InfoSection>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <InfoRow
                  label="Joined org"
                  value={detail.profile.joinedAt ? formatDate(detail.profile.joinedAt) : '—'}
                />
                {detail.profile.accountCreatedAt && (
                  <InfoRow label="Account created" value={formatDate(detail.profile.accountCreatedAt)} />
                )}
                {detail.profile.lastSignInAt && (
                  <InfoRow
                    label="Last sign-in"
                    value={
                      <span title={formatDate(detail.profile.lastSignInAt)}>
                        {formatRelative(detail.profile.lastSignInAt)}
                      </span>
                    }
                  />
                )}
                <InfoRow
                  label="Email verified"
                  value={
                    detail.profile.emailVerified === false ? (
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <ShieldAlert size={11} />
                        Not verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck size={11} />
                        Verified
                      </span>
                    )
                  }
                />
                {detail.profile.locale && (
                  <InfoRow
                    label="Locale"
                    value={
                      <span className="flex items-center gap-1">
                        <Globe size={11} />
                        {detail.profile.locale}
                      </span>
                    }
                  />
                )}
              </div>
            </InfoSection>
          </ModalSection>

          {/* Lifetime stats */}
          <ModalSection title="Lifetime Activity" icon={<Zap size={12} />}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Messages"
                value={detail.lifetime.messages}
                icon={<MessageSquare size={12} />}
              />
              <StatCard
                label="Sessions"
                value={detail.lifetime.sessions}
                icon={<Activity size={12} />}
              />
              <StatCard
                label="Files"
                value={detail.lifetime.files}
                icon={<Paperclip size={12} />}
              />
              <StatCard
                label="Last active"
                value={formatRelative(detail.lifetime.lastActive)}
                icon={<Clock size={12} />}
              />
            </div>
          </ModalSection>

          {/* Activity trend */}
          <ModalSection title="Activity Trend" icon={<Activity size={12} />}>
            <InfoSection>
              <TrendChart key={detail.profile.userId} userId={detail.profile.userId} />
            </InfoSection>
          </ModalSection>

          {/* Hourly + day-of-week heatmaps — 2/3 + 1/3 side by side */}
          <div className="grid min-h-0 grid-cols-[2fr_1fr] items-stretch gap-4">
            <InfoSection className="flex h-full min-h-0 flex-col gap-0">
              <HourlyWeekHeatmap
                key={detail.profile.userId}
                hourlyByDay={detail.chat.hourlyByDay}
              />
            </InfoSection>
            <InfoSection className="flex h-full min-h-0 flex-col gap-0">
              <WeeklyDayHeatmap
                key={detail.profile.userId}
                weeklyActivity={detail.chat.weeklyActivity}
              />
            </InfoSection>
          </div>

          {/* Chat overview */}
          <ModalSection title="Chat Overview" icon={<MessageSquare size={12} />}>
            <InfoSection>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-xs">
                <InfoRow label="Total sessions" value={String(detail.chat.totalSessions)} />
                <InfoRow label="Archived" value={String(detail.chat.archivedSessions)} />
                <InfoRow label="Avg messages / session" value={String(detail.chat.avgMessagesPerSession)} />
                <InfoRow label="Your messages" value={String(detail.chat.totalUserMessages)} />
                <InfoRow label="AI responses" value={String(detail.chat.totalAiMessages)} />
                <InfoRow label="With page context" value={String(detail.chat.pageContextCount)} />
              </div>

              {engagementItems.length > 0 && (
                <div className="flex flex-col gap-1.5 pt-1">
                  <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-semibold">
                    Engagement mix
                  </span>
                  <CompositionBar items={engagementItems} />
                </div>
              )}

              {detail.chat.truncated && (
                <p className="text-[10px] text-muted-foreground/60 pt-1">
                  Analytics based on {detail.chat.sessionsAnalyzed} most recent of {detail.chat.sessionsTotal} sessions.
                </p>
              )}
            </InfoSection>
          </ModalSection>

          {/* AI Specialists */}
          <ModalSection title="AI Specialists" icon={<Brain size={12} />}>
            <InfoSection>
              <SpecialistsChart specialists={detail.chat.specialists} />
            </InfoSection>
          </ModalSection>

          {/* Files */}
          {(detail.chat.files.total > 0) && (
            <ModalSection title="Attached Files" icon={<Paperclip size={12} />}>
              <InfoSection>
                <div className="flex items-center gap-4 text-xs">
                  <InfoRow label="Total files" value={String(detail.chat.files.total)} />
                  <InfoRow
                    label="Total size"
                    value={`${detail.chat.files.totalSizeMb.toFixed(1)} MB`}
                  />
                </div>
                {detail.chat.files.byType.length > 0 && (
                  <div className="flex flex-col gap-1.5 pt-1">
                    <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-semibold">
                      By type
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {detail.chat.files.byType.map((ft) => (
                        <span
                          key={ft.contentType}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground"
                        >
                          {ft.contentType} <span className="text-foreground font-medium">{ft.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </InfoSection>
            </ModalSection>
          )}

          {/* Recent sessions */}
          {detail.chat.recentSessions.length > 0 && (
            <ModalSection title="Recent Sessions" icon={<FileText size={12} />}>
              <InfoSection>
                <div className="flex flex-col divide-y divide-border/40">
                  {detail.chat.recentSessions.map(session => (
                    <div key={session.sessionId} className="flex items-start gap-2 py-2.5 first:pt-0 last:pb-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium truncate">{session.title}</span>
                          {session.aiSkillName && (
                            <Badge tone="primary">{session.aiSkillName}</Badge>
                          )}
                          {session.isArchived && (
                            <Badge tone="neutral">
                              <Archive size={9} />
                              Archived
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-[10px] text-muted-foreground/60" title={formatDate(session.createdAt)}>
                            {formatRelative(session.createdAt)}
                          </span>
                          <span className="text-[10px] text-muted-foreground/50">
                            {session.messageCount} msg{session.messageCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </InfoSection>
            </ModalSection>
          )}
        </div>
      )}
    </Dialog>
  );
};
